/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import _ from 'lodash';
import dayjs from 'dayjs';
import {Button, DatePicker, Input, InputNumber, Badge, Popover, Space} from 'antd';
import {CopyOutlined, DownOutlined, RightOutlined, SearchOutlined} from '@ant-design/icons';
import styles from './PointAdjustTable.module.scss';
import {PointAdjustHistory, PointAdjustRankingData, PointAdjustTableColumns} from '../PointAdjust.interface';
import PointAdjustHistoryTable from './PointAdjustHistoryTable/PointAdjustHistoryTable';
import userAPI from '../../../api/userAPI';
import {GroupValue} from "../../../components/common/selector/GroupSearchSelect";
import constants from "../../../constants";

interface PointAdjustTableProps {
	filteredRankingData: PointAdjustRankingData[];
	setFilteredRankingData: React.Dispatch<React.SetStateAction<PointAdjustRankingData[]>>;
	deleteHistory: (id: string) => any;
	editHistory: (history: PointAdjustHistory) => any;
	onClickAdjustPointBtn: (record: PointAdjustRankingData) => any;
	selectedGroup: GroupValue;
}

const useUpdateField = (setFilteredRankingData: React.Dispatch<React.SetStateAction<PointAdjustRankingData[]>>) => {
	return useCallback(
		(memberCarId: string, key: keyof PointAdjustRankingData, value: any) => {
			setFilteredRankingData(prev =>
				prev.map(item => {
					if (item.memberCarId !== memberCarId) return item;
					if (value === undefined) return _.omit(item, [key as string]) as PointAdjustRankingData;
					return { ...item, [key]: value } as PointAdjustRankingData;
				}),
			);
		},
		[setFilteredRankingData],
	);
};

const EditableCell = React.memo(
	({
		 type,
		 value,
		 onChange,
		 onKeyDown,
		 dateFormat,
	 }: {
		type: 'input' | 'number' | 'date';
		value: any;
		onChange: (v: any) => void;
		onKeyDown?: (e: any) => void;
		dateFormat?: string;
	}) => {
		if (type === 'input')
			return <Input style={{ textAlign: 'left' }} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
		if (type === 'number')
			return (
				<InputNumber
					style={{ textAlign: 'center', width: '100%' }}
					value={value}
					onKeyDown={onKeyDown}
					onChange={v => onChange(v || 0)}
				/>
			);
		if (type === 'date')
			return (
				<DatePicker
					value={value ? dayjs(value.toString(), dateFormat) : null}
					format={dateFormat}
					allowClear={false}
					onChange={(date, dateString) => onChange(dateString ? Number(dateString) : undefined)}
				/>
			);
		return null;
	},
	(prev, next) => _.isEqual(prev.value, next.value),
);

const PointAdjustTable = ({
	                          filteredRankingData,
	                          setFilteredRankingData,
	                          deleteHistory,
	                          editHistory,
	                          onClickAdjustPointBtn,
	                          selectedGroup,
                          }: PointAdjustTableProps) => {
	const updateField = useUpdateField(setFilteredRankingData);
	const parentRef = useRef<HTMLDivElement>(null);
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	// 검색 상태
	const [nameSearch, setNameSearch] = useState('');
	const [nicknameSearch, setNicknameSearch] = useState('');

	// 팝업 관련
	const [openPopover, setOpenPopover] = useState<string | null>(null);
	const [popupData, setPopupData] = useState<any>(undefined);
	const popupCacheRef = useRef<Record<string, any>>({});

	// 🔍 검색 필터 적용
	const visibleData = useMemo(() => {
		const nameTerm = nameSearch.trim().toLowerCase();
		const nickTerm = nicknameSearch.trim().toLowerCase();

		return filteredRankingData.filter(item => {
			const nameMatch = !nameTerm || (item.name ?? '').toLowerCase().includes(nameTerm);
			const nickMatch = !nickTerm || (item.nickname ?? '').toLowerCase().includes(nickTerm);
			return nameMatch && nickMatch;
		});
	}, [filteredRankingData, nameSearch, nicknameSearch]);

	const rowVirtualizer = useVirtualizer({
		count: visibleData.length,
		getScrollElement: () => parentRef.current,
		estimateSize: i => {
			const record = visibleData[i];
			const base = 60;
			const hasHistory = (record.pointAdjustHistory?.length ?? 0) > 0;
			const isExpanded = expandedRows.has(record.memberCarId);
			const historyHeight =
				isExpanded && hasHistory ? record.pointAdjustHistory.length * base + 80 : 0;
			return base + historyHeight;
		},
		getItemKey: index => visibleData[index].memberCarId,
		overscan: 5,
	});

	const preventInputDecimal = (e: any) => {
		if (e.key === '.' || e.key === 'Decimal') e.preventDefault();
	};

	const getRowEditButtonDisabled = (record: PointAdjustRankingData) => {
		const { adjustPoint, adjustBaljuCount, adjustSujuCount, adjustBaljuAmount, adjustSujuAmount, adjustReason, adjustDate } =
			record;
		const isDateValid = adjustDate && dayjs(adjustDate.toString(), 'YYYYMMDD').isValid();
		const allEmpty = !adjustPoint && !adjustBaljuCount && !adjustSujuCount && !adjustBaljuAmount && !adjustSujuAmount;
		return !isDateValid || allEmpty || !adjustReason;
	};

	const toggleExpandRow = (id: string) => {
		setExpandedRows(prev => {
			const newSet = new Set(prev);
			if (newSet.has(id)) newSet.delete(id);
			else newSet.add(id);
			return newSet;
		});
	};

	const onOpenPopup = async (bool: boolean, memberCarId: string, userId?: string) => {
		if (bool && memberCarId && userId) {
			if (popupCacheRef.current[userId]) {
				setPopupData(popupCacheRef.current[userId]);
				setOpenPopover(memberCarId);
				return;
			}
			try {
				const res = await userAPI.fetchUserDataAPI(userId);
				popupCacheRef.current[userId] = res;
				setPopupData(res);
				setOpenPopover(memberCarId);
			} catch (e) {
				console.error('Failed to fetch user data:', e);
			}
		} else {
			setPopupData(undefined);
			setOpenPopover(null);
		}
	};

	const onClosePopup = (e: any) => {
		e.preventDefault();
		setOpenPopover(null);
		setPopupData(undefined);
	};

	useEffect(() => {
		rowVirtualizer.measure();
	}, [expandedRows, visibleData.length]);

	return (
		<div className={styles.virtualTableContainer}>
			{/* 🔍 검색 입력 필드 */}
			<div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
				<Input
					allowClear
					placeholder="이름 검색"
					prefix={<SearchOutlined />}
					value={nameSearch}
					onChange={e => setNameSearch(e.target.value)}
					style={{ width: 200 }}
				/>
				<Input
					allowClear
					placeholder="닉네임 검색"
					prefix={<SearchOutlined />}
					value={nicknameSearch}
					onChange={e => setNicknameSearch(e.target.value)}
					style={{ width: 200 }}
				/>
			</div>

			<div ref={parentRef} className={styles.tableScrollArea}>
				{/* Sticky Header */}
				<div className={`${styles.row} ${styles.headerRow}`} style={{ width: 'max-content' }}>
					{PointAdjustTableColumns.map(col => (
						<div key={col.key} className={styles.cell} style={{ width: col.width }}>
							{col.title}
						</div>
					))}
				</div>

				{/* Virtualized Rows */}
				<div
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`,
						position: 'relative',
						width: 'max-content',
					}}
				>
					{rowVirtualizer.getVirtualItems().map(virtualRow => {
						const record = visibleData[virtualRow.index];
						const hasHistory = record.pointAdjustHistory?.length > 0;
						const isExpanded = expandedRows.has(record.memberCarId);

						return (
							<div
								key={record.memberCarId}
								className={styles.row}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: 'max-content',
									transform: `translateY(${virtualRow.start}px)`,
								}}
							>
								<div
									className={styles.cell}
									style={{ width: PointAdjustTableColumns[0].width, textAlign: 'center', cursor: hasHistory ? 'pointer' : 'default' }}
									onClick={() => hasHistory && toggleExpandRow(record.memberCarId)}
								>
									{hasHistory ? (
										<div className={styles.historyIndicator}>
											{isExpanded ? <DownOutlined /> : <RightOutlined />}
											<Badge count={record.pointAdjustHistory.length} size="small" color={isExpanded ? '#1890ff' : '#999'} />
										</div>
									) : (
										<span style={{ color: '#aaa' }}>-</span>
									)}
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[1].width }}>{record.rank}</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[2].width, flexDirection: 'column' }}>
									<div>{record.carType}</div>
									<div>{record.number}</div>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[3].width }}>
									<Popover
										placement="top"
										content={
											<a onClick={onClosePopup}>
												{popupData?.memberInfo?.filter((item: any) => item.status === 'ok').map((item: any) => (
													<div key={item.name}>{item.name}</div>
												))}
											</a>
										}
										trigger="click"
										title={
											<div style={{ display: 'flex', justifyContent: 'space-between' }}>
												<div>{popupData?.userInfo?.name || ''}</div>
												<div>{popupData?.userInfo?.phoneNum || ''}</div>
											</div>
										}
										open={openPopover === record.memberCarId}
										onOpenChange={(bool, e) => {
											e?.stopPropagation();
											onOpenPopup(bool, record.memberCarId, record.userId);
										}}
									>
										<div className={styles.clickable} onClick={e => e.stopPropagation()}>
											<div>{record.name}</div>
											<div>{record.nickname}</div>
										</div>
									</Popover>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[4].width }}>{record.point}</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[5].width }}>{record.baljuCount}</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[6].width }}>{record.sujuCount}</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[7].width }}>
									<EditableCell
										type="date"
										value={record.adjustDate}
										onChange={v => updateField(record.memberCarId, 'adjustDate', v)}
										dateFormat="YYYYMMDD"
									/>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[8].width }}>
									<EditableCell
										type="number"
										value={record.adjustPoint}
										onKeyDown={preventInputDecimal}
										onChange={v => updateField(record.memberCarId, 'adjustPoint', v)}
									/>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[9].width }}>
									<EditableCell
										type="number"
										value={record.adjustBaljuCount}
										onKeyDown={preventInputDecimal}
										onChange={v => updateField(record.memberCarId, 'adjustBaljuCount', v)}
									/>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[10].width }}>
									<EditableCell
										type="number"
										value={record.adjustSujuCount}
										onKeyDown={preventInputDecimal}
										onChange={v => updateField(record.memberCarId, 'adjustSujuCount', v)}
									/>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[11].width }}>
									{selectedGroup.key === constants.CALL_CENTER_GROUP_ID
										&& (
											<CopyOutlined
												style={{ marginRight: '10px', cursor: 'pointer' }}
												onClick={() => updateField(record.memberCarId, 'adjustReason', '추가점수이벤트')}
											/>
										)}
									<EditableCell
										type="input"
										value={record.adjustReason}
										onChange={v => updateField(record.memberCarId, 'adjustReason', v)}
									/>
								</div>
								<div className={styles.cell} style={{ width: PointAdjustTableColumns[12].width }}>
									<Button
										size="small"
										disabled={getRowEditButtonDisabled(record)}
										onClick={() => onClickAdjustPointBtn(record)}
									>
										추가
									</Button>
								</div>

								{isExpanded && hasHistory && (
									<div className={styles.historyTableWrapper}>
										<PointAdjustHistoryTable
											record={record}
											filteredRankingData={filteredRankingData}
											setFilteredRankingData={setFilteredRankingData}
											deleteHistory={deleteHistory}
											editHistory={editHistory}
										/>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default PointAdjustTable;
